'use client';
import { useCallback, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';

interface RelatedMarketItem {
  id: string;
  icon: string;
  text: string;
  percentage: number;
}

interface RelatedMarketsSectionProps {
  items?: RelatedMarketItem[];
}

const DEFAULT_ITEMS: RelatedMarketItem[] = [
  {
    id: '1',
    icon: '🇺🇦',
    text: 'Russia x Ukraine Peace Parlay',
    percentage: 22,
  },
  {
    id: '2',
    icon: '🕊️',
    text: 'Ukraine officially agrees to a US backed ceasefire framework by...',
    percentage: 33,
  },
  {
    id: '3',
    icon: '🇷🇺',
    text: 'Ukraine recognizes Russian sovereignty over its territory by...',
    percentage: 7,
  },
];

const TABS = ['All', 'Geopolitics', 'Ukraine', 'Politics', 'World'] as const;

const RelatedMarketsSection = function RelatedMarketsSection({
  items = DEFAULT_ITEMS,
}: RelatedMarketsSectionProps) {
  const [activeTab, setActiveTab] = useState<string>('All');

  const handleTabClick = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        p: 2.5,
      }}
    >
      {/* Tabs */}
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px dashed',
          borderColor: 'divider',
          mb: 2,
          overflowX: 'auto',
          pb: 1,
        }}
      >
        {TABS.map((tab) => (
          <Button
            key={tab}
            variant="text"
            onClick={() => handleTabClick(tab)}
            sx={{
              textTransform: 'none',
              fontWeight: activeTab === tab ? 600 : 400,
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              minWidth: 'auto',
              color: activeTab === tab ? 'text.primary' : 'text.secondary',
              bgcolor: activeTab === tab ? 'action.hover' : 'transparent',
              borderRadius: 1,
              '&:hover': {
                bgcolor: activeTab === tab ? 'action.hover' : 'action.hover',
              },
            }}
          >
            {tab}
          </Button>
        ))}
      </Box>

      {/* List Items */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                flexShrink: 0,
                bgcolor: 'action.hover',
              }}
            >
              {item.icon}
            </Box>

            {/* Text */}
            <Typography
              sx={{
                flex: 1,
                fontSize: '0.875rem',
                fontWeight: 400,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.text}
            </Typography>

            {/* Percentage */}
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'text.primary',
                flexShrink: 0,
              }}
            >
              {item.percentage}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export { RelatedMarketsSection };
export default RelatedMarketsSection;
